import { Card, Empty } from 'antd';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { defaultBudgetItemsConfig } from '../../../../../../types/apiTypes';
import { unicodeToEmoji } from '../../../../../../shared/emojiUnicode';
import { fmtCents } from './shared/formatCents';

const COLORS = [
  '#1890ff', // fuel — blue
  '#52c41a', // PM — green
  '#faad14', // tires — gold
  '#f5222d', // repairs — red
  '#722ed1', // tolls — purple
  '#13c2c2', // insurance — cyan
  '#eb2f96', // licensing — magenta
  '#fa8c16', // truck_payments — orange
  '#8c8c8c', // fallback — gray
];

type Slice = {
  category: string;
  label: string;
  amount: number;
  color: string;
  pct: number;
};

function buildSlices(byCategory: Record<string, number>): Slice[] {
  const total = Object.values(byCategory).reduce((s, v) => s + v, 0);
  if (total === 0) return [];

  const configByName = Object.fromEntries(
    Object.values(defaultBudgetItemsConfig).map((item) => [item.category, item])
  );

  return Object.entries(byCategory)
    .filter(([, amount]) => amount > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([category, amount], i) => {
      const cfg = configByName[category];
      const label = cfg
        ? `${unicodeToEmoji(cfg.emoji_unicode)} ${cfg.category_display_name}`
        : category.replace(/_/g, ' ');
      return {
        category,
        label,
        amount,
        color: COLORS[i % COLORS.length],
        pct: amount / total,
      };
    });
}

function PieTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const slice = payload[0].payload as Slice;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 4,
        padding: '8px 12px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{slice.label}</div>
      <div>{fmtCents(slice.amount)}</div>
      <div style={{ color: '#6b7280' }}>
        {(slice.pct * 100).toFixed(1)}% of total
      </div>
    </div>
  );
}

export function ExpenseBreakdownPie({
  byCategory,
  year,
}: {
  byCategory: Record<string, number> | undefined;
  year: number;
}) {
  const slices = byCategory ? buildSlices(byCategory) : [];

  return (
    <Card title={`Expense Breakdown — ${year}`}>
      {slices.length === 0 ? (
        <Empty description="No expenses to display" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="amount"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={110}
                  paddingAngle={2}
                >
                  {slices.map((s) => (
                    <Cell key={s.category} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px 16px',
              justifyContent: 'center',
              marginTop: 16,
              fontSize: 13,
            }}
          >
            {slices.map((s) => (
              <span
                key={s.category}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: s.color,
                    display: 'inline-block',
                  }}
                />
                {s.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
